/**
 * Pure derivations over official weather warnings: which are current, which
 * are in force now, how bad it gets, and which rows are one hazard.
 *
 * Every function takes `now` (epoch SECONDS, the API's unit) instead of reading
 * the clock, so the boundaries are testable and both apps agree on them.
 *
 * Interval convention: a warning is in force on `[start_time, end_time)`. It is
 * active when `start_time <= now < end_time`, upcoming when `now < start_time`,
 * and expired (dropped) when `end_time <= now`. A null `end_time` never ends
 * ("until further notice"). The backend caches the point response for up to
 * an hour, so the client must do this filtering itself.
 */
import type {
  WarningSeverity,
  WarningType,
  WarningsSummaryItem,
  WeatherWarning,
} from '../../api/types';

/** Unknown ranks lowest: it must never outrank a known level. */
const SEVERITY_RANK: Record<WarningSeverity, number> = {
  unknown: 0,
  minor: 1,
  moderate: 2,
  severe: 3,
  extreme: 4,
};

export const severityRank = (severity: WarningSeverity): number =>
  SEVERITY_RANK[severity] ?? 0;

/** The 14 `WeatherWarningType` values of the backend model. */
export const WARNING_TYPES: readonly WarningType[] = [
  'wind',
  'rain',
  'thunderstorm',
  'flood',
  'snow_ice',
  'avalanche',
  'coastal_event',
  'fog',
  'high_temperature',
  'low_temperature',
  'forest_fire',
  'uv',
  'air_quality',
  'other',
];

/** A type the app does not know yet (a newer backend) reads as `other`. */
export const normaliseType = (type: string): WarningType =>
  (WARNING_TYPES as readonly string[]).includes(type)
    ? (type as WarningType)
    : 'other';

const worse = (a: WarningSeverity, b: WarningSeverity) =>
  severityRank(b) > severityRank(a) ? b : a;

/** A finite end, else null: "until further notice". */
const endOf = (w: WeatherWarning): number | null =>
  typeof w.end_time === 'number' && Number.isFinite(w.end_time)
    ? w.end_time
    : null;

/** Null `end_time` = in force until further notice, never expired. */
export const isExpired = (w: WeatherWarning, now: number) => {
  const end = endOf(w);
  return end !== null && end <= now;
};

export const isActive = (w: WeatherWarning, now: number) =>
  w.start_time <= now && !isExpired(w, now);

/** Drops every warning that has ended (`end_time <= now`); keeps input order. */
export const currentWarnings = (
  warnings: readonly WeatherWarning[],
  now: number,
): WeatherWarning[] => warnings.filter((w) => !isExpired(w, now));

/** Current warnings split into in force now and not started yet. */
export const splitActiveUpcoming = (
  warnings: readonly WeatherWarning[],
  now: number,
): {active: WeatherWarning[]; upcoming: WeatherWarning[]} => {
  const active: WeatherWarning[] = [];
  const upcoming: WeatherWarning[] = [];
  for (const w of currentWarnings(warnings, now)) {
    (isActive(w, now) ? active : upcoming).push(w);
  }
  return {active, upcoming};
};

/**
 * The worst level in force now, and the worst level of everything current
 * (now or later). They differ when a hazard escalates: "Severe now · Extreme
 * from 12:00" is `{active: 'severe', coming: 'extreme'}`.
 */
export const highestSeverity = (
  warnings: readonly WeatherWarning[],
  now: number,
): {active: WarningSeverity | null; coming: WarningSeverity | null} => {
  let active: WarningSeverity | null = null;
  let coming: WarningSeverity | null = null;
  for (const w of currentWarnings(warnings, now)) {
    coming = coming === null ? w.severity : worse(coming, w.severity);
    if (isActive(w, now)) {
      active = active === null ? w.severity : worse(active, w.severity);
    }
  }
  return {active, coming};
};

/**
 * One spell at one level: a warning row, or several rows of the same level
 * that follow on from each other (see `groupByHazard`).
 */
export type HazardSlice = {
  /** The earliest row of the spell: its id, headline and source stand for it. */
  warning: WeatherWarning;
  /** Every row merged into the spell, by start. */
  warnings: WeatherWarning[];
  severity: WarningSeverity;
  start: number;
  /** Null: until further notice. */
  end: number | null;
  active: boolean;
};

export type HazardGroup = {
  /** Stable React key: the hazard, or `other:<headline>` for `other`. */
  key: string;
  hazard: WarningType;
  /** The worst level of any of its slices. */
  severity: WarningSeverity;
  /** True when any slice is in force now. */
  active: boolean;
  start: number;
  /** Null when any slice runs until further notice. */
  end: number | null;
  /** Ordered by start, then end, then id. */
  slices: HazardSlice[];
};

/**
 * Two rows of one level at most this far apart are one spell. AEMET ends a
 * row at 23:59:59 and starts the next at 00:00:00 (1 s); other MeteoAlarm
 * members end at 23:59 (60 s) or butt the rows exactly (0 s). Measured on
 * production on 2026-10-04: 154 + 176 + 25 such pairs, none in between.
 */
export const MERGE_GAP_SECONDS = 60;

const OPEN = Number.POSITIVE_INFINITY;
const sortEnd = (end: number | null) => end ?? OPEN;

const compareWarnings = (a: WeatherWarning, b: WeatherWarning) =>
  a.start_time - b.start_time ||
  sortEnd(endOf(a)) - sortEnd(endOf(b)) ||
  a.id - b.id;

/**
 * Folds rows of one hazard into spells: a row that starts before the latest
 * spell of its level ends, or at most `MERGE_GAP_SECONDS` after, extends it.
 * Spells stay ordered by start (a spell keeps its first row's). Without this, a moderate episode AEMET splits at midnight
 * reads "Moderate until today 23:59" when it lasts until tomorrow 20:59.
 * Rows of different levels are never merged: each level change is a slice.
 */
const mergeSpells = (
  warnings: readonly WeatherWarning[],
  now: number,
): HazardSlice[] => {
  const spells: HazardSlice[] = [];
  for (const w of [...warnings].sort(compareWarnings)) {
    // The latest spell at this level, even if a row of another level started
    // since (a severe hour inside a long moderate day).
    let last: HazardSlice | undefined;
    for (let i = spells.length - 1; i >= 0 && !last; i--) {
      if (spells[i].severity === w.severity) {
        last = spells[i];
      }
    }
    if (
      last &&
      (last.end === null || w.start_time <= last.end + MERGE_GAP_SECONDS)
    ) {
      const end = endOf(w);
      last.end =
        last.end === null || end === null ? null : Math.max(last.end, end);
      last.warnings.push(w);
    } else {
      spells.push({
        warning: w,
        warnings: [w],
        severity: w.severity,
        start: w.start_time,
        end: endOf(w),
        active: false,
      });
    }
  }
  for (const spell of spells) {
    spell.active =
      spell.start <= now && (spell.end === null || now < spell.end);
  }
  return spells;
};

/**
 * The order of hazard groups, worst first: the card lists them in it and its
 * band names the first (the LEAD hazard); `warningsSummaryItem` and the
 * backend's `GET /api/warnings/summary/` must pick the same lead, so the rule
 * is spelled out here for the backend to mirror:
 *
 * 1. Worse `severity` first, by `severityRank` (unknown < minor < moderate <
 *    severe < extreme). A group's severity is the worst level of ANY of its
 *    current slices, upcoming ones included.
 * 2. At equal severity, a group in force now (any slice active) before one
 *    that only starts later.
 * 3. Then the earlier `start`: the earliest start (epoch seconds) of its
 *    slices. (Given rule 2's input, an active group always starts before an
 *    upcoming one, so 2 never contradicts 3.)
 * 4. Then the group `key`, compared by UTF-16 code unit (JS `<`, not
 *    locale): the `warning_type`, or `other:<headline>` for `other`, so the
 *    order never depends on the input order. Python's `str` comparison is by
 *    code point and differs only for a character outside the BMP against
 *    U+E000–U+FFFF in a headline; compare `key.encode('utf-16-be')` to match
 *    exactly.
 */
export const compareHazardGroups = (a: HazardGroup, b: HazardGroup) =>
  severityRank(b.severity) - severityRank(a.severity) ||
  Number(b.active) - Number(a.active) ||
  a.start - b.start ||
  (a.key < b.key ? -1 : a.key > b.key ? 1 : 0);

/**
 * Folds the current warnings into one group per hazard.
 *
 * AEMET (via MeteoAlarm) issues one rain episode as several rows, one per
 * severity slice (severe 15:00–23:59, extreme 00:00–20:59, moderate after):
 * read row by row they look like three unrelated warnings. Grouping by
 * `warning_type` gives the episode back. `other` is the exception: it is a
 * catch-all for unrelated products (a special weather statement, a tsunami
 * bulletin), so it is grouped by headline instead. Within a group, rows of
 * one level that follow on from each other are one slice (`mergeSpells`).
 */
export const groupByHazard = (
  warnings: readonly WeatherWarning[],
  now: number,
): HazardGroup[] => {
  const byKey = new Map<
    string,
    {hazard: WarningType; rows: WeatherWarning[]}
  >();
  for (const w of currentWarnings(warnings, now)) {
    const hazard = normaliseType(w.warning_type);
    const key = hazard === 'other' ? `other:${w.headline ?? ''}` : hazard;
    const entry = byKey.get(key);
    if (entry) {
      entry.rows.push(w);
    } else {
      byKey.set(key, {hazard, rows: [w]});
    }
  }
  const result: HazardGroup[] = [];
  for (const [key, {hazard, rows}] of byKey) {
    // In row order (start, end, id), and a spell keeps its first row's start.
    const slices = mergeSpells(rows, now);
    let end: number | null = slices[0].end;
    for (const slice of slices) {
      end =
        end === null || slice.end === null ? null : Math.max(end, slice.end);
    }
    result.push({
      key,
      hazard,
      severity: slices.reduce<WarningSeverity>(
        (acc, s) => worse(acc, s.severity),
        slices[0].severity,
      ),
      active: slices.some((s) => s.active),
      start: Math.min(...slices.map((s) => s.start)),
      end,
      slices,
    });
  }
  return result.sort(compareHazardGroups);
};

/**
 * The slice a summary names: the worst level, then the earliest start. The
 * card's band quotes its time, and its first row's `severity_code` is the
 * summary's.
 */
export const worstSlice = (slices: readonly HazardSlice[]): HazardSlice =>
  slices.reduce((best, s) => {
    const diff = severityRank(s.severity) - severityRank(best.severity);
    return diff > 0 || (diff === 0 && s.start < best.start) ? s : best;
  });

/**
 * One point's entry of `GET /api/warnings/summary/`, computed from the point
 * endpoint's rows: the reference the backend mirrors, and what a badge built
 * from the point endpoint shows.
 *
 * - `severity`, `warning_type`: the lead hazard's, exactly as the dashboard
 *   card's band picks it (the first group in `compareHazardGroups` order).
 * - `severity_code`: the first row of the lead's worst slice (`worstSlice`).
 * - `count`: distinct hazards after `groupByHazard` (an AEMET episode split
 *   into three rows counts once), not rows.
 * - `active`: at least one current warning has started (any group in force
 *   now), whichever hazard leads: the summary contract's definition. It can
 *   be true while the lead's worst slice is still upcoming (moderate wind
 *   now, severe snow from 12:00: the card's band reads "Severe … from
 *   12:00"). False when all are upcoming: the badge then draws an outlined
 *   chip.
 *
 * Null when no warning is current at `now`.
 */
export const warningsSummaryItem = (
  warnings: readonly WeatherWarning[],
  now: number,
): WarningsSummaryItem | null => {
  const groups = groupByHazard(warnings, now);
  if (groups.length === 0) {
    return null;
  }
  const lead = groups[0];
  return {
    severity: lead.severity,
    severity_code: worstSlice(lead.slices).warning.severity_code ?? null,
    warning_type: lead.hazard,
    count: groups.length,
    active: groups.some((g) => g.active),
  };
};
